import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { Link } from "react-router";
import styled, { css } from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { countries } from "@domain/currentProfile/countryCode";
import { useIsPerson } from "@domain/currentProfile/hooks";
import { Experience } from "@domain/experience/experience";
import { formatMonthAndYear } from "@utils/date";
import { useIsMobile } from "@utils/hooks";
import { useModalTools } from "@ui/modals/modalContext";
import { useConfirmation } from "@ui/modals/useConfirmation";
import { gap } from "@ui/styles/utils";
import { PrimaryBold } from "@ui/components/containers/primary";
import { Secondary } from "@ui/components/containers/textBlocks";
import { CalendarIcon, DomainIcon, HatIcon, MapIcon } from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { ProfilePicture } from "@ui/components/profile/profilePicture";
import { ExperienceEditionModal } from "./experienceEditionModal";

interface ExperienceViewProps extends React.HTMLAttributes<HTMLDivElement> {
	experience: Experience;
	profileId: string;
}
export const ExperienceView: React.FC<ExperienceViewProps> = ({ experience, profileId, ...htmlProps }) => {
	const { createModal } = useModalTools();
	const { experienceService } = useServices();
	const { id, title, asTeacher, start, end, type, organization, countryCode, domain, diploma } = experience;
	const isMe = useIsPerson({ id: profileId });
	const displayCountry = countryCode ? `, ${countries[countryCode]}` : "";
	const askDeleteConfirmation = useConfirmation(I18n.t("experience.delete.title"), I18n.t("delete"));
	const [isHovered, setIsHovered] = useState(false);
	const isMobile = useIsMobile();

	return (
		<Container {...htmlProps} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
			{!end || asTeacher ? (
				<Labels>
					{!end && <Label>{I18n.t(`experience.${type.toLowerCase()}.info.current`)}</Label>}
					{asTeacher && <Label colored>{I18n.t("experience.info.teacher")}</Label>}
				</Labels>
			) : null}
			<OrganizationView>
				{organization.id ? (
					<Link to={Path.Organization(organization)}>
						<ProfilePicture
							picture={organization.avatar}
							size={36}
							fallbackType={type === "ACADEMIC" ? "INSTITUTE" : "COMPANY"}
						/>
					</Link>
				) : (
					<ProfilePicture size={36} fallbackType={type === "ACADEMIC" ? "INSTITUTE" : "COMPANY"} />
				)}
				<Title>{title}</Title>
			</OrganizationView>
			<Details disabled={!organization.id}>
				{organization.primaryName && (
					<div>
						<MapIcon />
						<Link to={Path.Organization(organization)}>
							{organization.primaryName}
							{displayCountry}
						</Link>
					</div>
				)}
				<div>
					<CalendarIcon />
					<div>
						{formatMonthAndYear(start)} - {end ? formatMonthAndYear(end) : I18n.t("today")}
					</div>
				</div>
				{diploma && (
					<div>
						<BlackHatIcon />
						<div>{diploma}</div>
					</div>
				)}
				{domain && (
					<div>
						<DomainIcon width={20} />
						<div>{domain}</div>
					</div>
				)}
			</Details>
			{isMe && (
				<StyledKebabMenu isVisible={isMobile ? true : isHovered}>
					<SimpleMenuContent
						onEdit={() =>
							createModal((close) => <ExperienceEditionModal type={type} experience={experience} close={close} />)
						}
						onDelete={() => askDeleteConfirmation(() => experienceService.delete(id, type))}
					/>
				</StyledKebabMenu>
			)}
		</Container>
	);
};

const Container = styled.div`
	position: relative;
	${gap(20)};
`;

const OrganizationView = styled.div`
	${row("S", "flex-start", "center")};
	padding-right: 20px;
`;

const Title = styled(PrimaryBold)`
	font-size: 0.8125rem;
`;

const Labels = styled.div`
	display: flex;
	${gap(8, "row")};
`;

const Label = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "colored",
})<{ colored?: boolean }>`
	${({ theme }) => theme.textStyles.small};
	background-color: ${({ theme }) => theme.colors.bodyBackground};
	padding: 1px 5px;
	border-radius: 4px;

	${({ theme, colored }) =>
		colored &&
		css`
			color: ${theme.colors.secondary};
			background-color: ${theme.colors.secondaryLight};
		`}
`;

const Details = styled(Secondary)<{ disabled?: boolean }>`
	${({ disabled }) => disabled && "pointer-events:none"};
	color: ${({ theme }) => theme.colors.textPrimary};
	${gap(12)};
	font-size: 0.8125rem;

	> div {
		${row(8, "flex-start", "center")};
		svg {
			width: 13px;
		}
	}
`;

const BlackHatIcon = styled(HatIcon)`
	path {
		fill: ${({ theme }) => theme.colors.black};
	}
`;

const StyledKebabMenu = styled(KebabMenu)`
	position: absolute;
	top: 25px;
	right: 15px;
`;
